import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '@vibesites/theme-preferred/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-preferred/hooks/useRevealStyles';

interface PageBlurbProps {
  /** Property field name for the blurb (e.g. 'property_floorplans_blurb') */
  blurbField?: string;
  /** Unique prefix so each page's blurb token is independently editable */
  tokenPrefix?: string;
  /** Static fallback text when no property blurb is available */
  defaultText?: string;
}

/**
 * Build the richtext default as an embedded field-token (custom mode).
 * Using `defaultField` on richtext puts the inline editor in field-mapping
 * mode, so edits to the text box do not persist as VALUE overrides.
 */
function resolveBlurbDefault(blurbField?: string, defaultText?: string): string {
  if (defaultText) return defaultText;
  const field = blurbField || 'property_long_description';
  return `<span data-type="field-token" data-field="${field}"></span>`;
}

export function PageBlurb({ blurbField, tokenPrefix, defaultText }: PageBlurbProps) {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const prefix = tokenPrefix || 'pageblurb';
  const textPath = `${prefix}.text`;
  const resolvedDefault = resolveBlurbDefault(blurbField, defaultText);

  return (
    <section ref={sectionRef as React.RefObject<HTMLElement>} className="reveal-fade-up pt-10 pb-4 md:pt-14 md:pb-6 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path={textPath}
          label="Blurb Text"
          defaultValue={resolvedDefault}
          as="div"
          className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-0"
        />
      </div>

      {/* Schema-lint anchors for static analysis — hidden at runtime when a page-specific prefix is used */}
      {tokenPrefix ? (
        <div className="hidden" style={{ display: 'none' }}>
          <RichTextContent
            path="pageblurb.text"
            label="Blurb Text"
            defaultValue=""
            as="span"
          />
        </div>
      ) : null}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageBlurb',
  displayName: 'Page Blurb',
  tokens: {
    'pageblurb.text': {
      label: 'Blurb Text',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_long_description"></span>',
      suggestedFields: [
        'property_floorplans_blurb',
        'property_amenities_blurb',
        'property_gallery_blurb',
        'property_neighborhood_blurb',
        'property_virtualtours_blurb',
        'property_contactus_blurb',
        'property_reviews_blurb',
        'property_pets_blurb',
        'property_applynow_blurb',
        'property_residentaccess_blurb',
        'property_scheduletour_blurb',
        'property_interactivemap_blurb',
      ],
    },
  },
};

(PageBlurb as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PageBlurb;
